/**
 * Pure A1-notation helpers shared by the Excel client executors (browser) and
 * the `write_range_from_file` hydrate step (workflow server). No Office.js and
 * no Node imports — keep it that way so both bundles can use it.
 */

const CELL_RE = /^\$?([A-Za-z]{1,3})\$?(\d+)$/;
const A1_EXTENT_RE = /^\$?([A-Za-z]{1,3})\$?(\d+):\$?([A-Za-z]{1,3})\$?(\d+)$/;
const COLUMN_RE = /^\$?([A-Za-z]{1,3})$/;
const ROW_RE = /^\$?(\d+)$/;

/** Column letters → 1-based index (A=1, Z=26, AA=27). */
export function columnToIndex(letters: string): number {
  let index = 0;
  for (const ch of letters.toUpperCase()) {
    index = index * 26 + (ch.charCodeAt(0) - 64);
  }
  return index;
}

/** 1-based column index → letters (1=A, 26=Z, 27=AA). */
export function indexToColumn(index: number): string {
  let letters = "";
  let n = index;
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

/**
 * Split an optionally sheet-qualified address into its sheet and bare parts.
 *
 * Handles `Sheet1!A1:B2`, `'My Sheet'!A1`, `'O''Brien'!A1` (doubled quote is
 * an escaped quote), and numeric names like `1!A1:G30`. An address with no
 * `!` comes back with no sheet.
 */
export function splitSheetQualifiedAddress(address: string): {
  address: string;
  sheetName?: string;
} {
  const trimmed = address.trim();
  if (trimmed.startsWith("'")) {
    let name = "";
    let i = 1;
    while (i < trimmed.length) {
      const ch = trimmed[i];
      if (ch === "'") {
        if (trimmed[i + 1] === "'") {
          name += "'";
          i += 2;
          continue;
        }
        break;
      }
      name += ch;
      i += 1;
    }
    if (trimmed[i] === "'" && trimmed[i + 1] === "!") {
      return { sheetName: name, address: trimmed.slice(i + 2) };
    }
    return { address: trimmed };
  }
  const bang = trimmed.lastIndexOf("!");
  if (bang <= 0) {
    return { address: trimmed };
  }
  return {
    sheetName: trimmed.slice(0, bang),
    address: trimmed.slice(bang + 1),
  };
}

/**
 * Split a multi-area reference (`"B8:AB10,B12:AB13"`) into its areas,
 * trimmed and with empties dropped. Commas inside a quoted sheet name
 * (`'Q1, Q2'!A1`) do not split. A single-area address comes back as a
 * one-element array.
 */
export function splitRangeAreas(address: string): string[] {
  const areas: string[] = [];
  let current = "";
  let inQuotes = false;
  for (const ch of address) {
    if (ch === "'") {
      inQuotes = !inQuotes;
    }
    if (ch === "," && !inQuotes) {
      areas.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  areas.push(current);
  return areas.map((a) => a.trim()).filter((a) => a.length > 0);
}

/** Excel sheet names compare case-insensitively. */
export function isSameSheetName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/** Qualify a bare address with a sheet name, quoting the name when needed. */
export function qualifyAddress(sheetName: string, address: string): string {
  const needsQuotes = !/^[A-Za-z_][A-Za-z0-9_.]*$/.test(sheetName);
  const name = needsQuotes ? `'${sheetName.replace(/'/g, "''")}'` : sheetName;
  return `${name}!${address}`;
}

/**
 * Parse the row/col extent of a multi-cell A1 range (optionally
 * sheet-qualified). Returns null for a single-cell anchor or anything
 * unparseable (whole columns, named ranges) — callers auto-size from the
 * anchor in those cases.
 */
export function parseRangeExtent(
  range: string
): { rows: number; cols: number } | null {
  const m = splitSheetQualifiedAddress(range).address.match(A1_EXTENT_RE);
  if (!m) {
    return null;
  }
  const [, startCol = "", startRow = "", endCol = "", endRow = ""] = m;
  const rows = Math.abs(Number(endRow) - Number(startRow)) + 1;
  const cols = Math.abs(columnToIndex(endCol) - columnToIndex(startCol)) + 1;
  return { rows, cols };
}

/**
 * Corner cells of a plain A1 cell/range, or of a whole-column (`A:C`) or
 * whole-row (`3:5`) range, whose top-left is row 1 / column A. Only the
 * two-part form counts as whole columns or rows: a bare `Tax` is a name.
 */
function cornerCells(address: string): Array<{ col: number; row: number }> {
  const parts = address.split(":");
  if (parts.length > 2) {
    return [];
  }
  const cells = parts.map((part) => part.match(CELL_RE));
  if (cells.every((m) => m)) {
    return cells.map((m) => ({
      col: columnToIndex(m?.[1] ?? ""),
      row: Number(m?.[2]),
    }));
  }
  if (parts.length !== 2) {
    return [];
  }
  const columns = parts.map((part) => part.match(COLUMN_RE));
  if (columns.every((m) => m)) {
    return columns.map((m) => ({ col: columnToIndex(m?.[1] ?? ""), row: 1 }));
  }
  const rowRefs = parts.map((part) => part.match(ROW_RE));
  if (rowRefs.every((m) => m)) {
    return rowRefs.map((m) => ({ col: 1, row: Number(m?.[1]) }));
  }
  return [];
}

/**
 * The address of a `rows`×`cols` block anchored at the top-left cell of
 * `address`, e.g. (`C2:C1358`, 10, 1) → `C2:C11`, (`B:D`, 2, 2) → `B1:C2`.
 * Returns null for anything else (named ranges, multi-area references).
 */
export function resizedAddress(
  address: string,
  rows: number,
  cols: number
): string | null {
  const cells = cornerCells(splitSheetQualifiedAddress(address).address);
  if (cells.length === 0) {
    return null;
  }
  const startCol = Math.min(...cells.map((c) => c.col));
  const startRow = Math.min(...cells.map((c) => c.row));
  const start = `${indexToColumn(startCol)}${startRow}`;
  if (rows === 1 && cols === 1) {
    return start;
  }
  const end = `${indexToColumn(startCol + cols - 1)}${startRow + rows - 1}`;
  return `${start}:${end}`;
}
